<script setup lang="ts">
import EmailTemplatePreview from './EmailTemplatePreview.vue'
import type { EmailTemplateListItem } from '@/composables/emailContent'

const { t } = useI18n()
const { listTemplates, createFromTemplate } = useEmailContent()

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'select-template', templateId: string): void
  (e: 'create-blank'): void
}>()

const templates = ref<EmailTemplateListItem[]>([])
const isLoading = ref(false)
const creatingTemplateId = ref<string | null>(null)

const fetchTemplates = async () => {
  isLoading.value = true
  try {
    templates.value = await listTemplates()
  } catch {
    templates.value = []
  } finally {
    isLoading.value = false
  }
}

const handleSelectTemplate = async (templateId: string) => {
  if (creatingTemplateId.value) return
  creatingTemplateId.value = templateId
  try {
    const result = await createFromTemplate(templateId)
    emit('select-template', result.id)
  } catch {
    // silently fail
  } finally {
    creatingTemplateId.value = null
  }
}

const handleCreateBlank = () => {
  emit('create-blank')
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) fetchTemplates()
  }
)
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    size="full"
    :show-footer="false"
    content-class="!p-0 flex-1 overflow-hidden"
    @close="emit('close')"
  >
    <div class="flex h-full flex-col bg-muted">
      <div class="bg-white px-12 pb-7 pt-8">
        <div class="flex items-end justify-between">
          <div class="flex flex-col gap-5">
            <div class="flex items-center gap-3 text-sm">
              <BaseIcon name="HeroHomeIconSolid" class="h-5 w-5 text-muted-foreground" />
              <BaseIcon name="HeroChevronRightIconSolid" class="h-4 w-4 text-muted-foreground" />
              <span class="font-medium text-muted-foreground">
                {{ t('pages.email.emailTemplateSelectorModal.title') }}
              </span>
            </div>
            <h1 class="text-2xl font-bold text-foreground">
              {{ t('pages.email.emailTemplateSelectorModal.title') }}
            </h1>
          </div>
          <div class="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              class="bg-white text-foreground border-border hover:bg-accent"
              @click="emit('close')"
            >
              <span>{{ t('global.actions.back') }}</span>
            </Button>
            <Button variant="default-primary" @click="handleCreateBlank">
              {{ t('pages.email.emailTemplateSelectorModal.createBlank') }}
            </Button>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-auto px-12 pt-8">
        <div class="mb-4 text-base font-semibold text-foreground">
          {{ t('pages.email.emailTemplateSelectorModal.objective') }}
        </div>

        <div v-if="isLoading" class="flex h-60 items-center justify-center">
          <div class="h-6 w-6 animate-spin rounded-full border-2 border-primary/15 border-t-primary" />
        </div>

        <div v-else-if="templates.length === 0" class="flex h-60 flex-col items-center justify-center">
          <span class="text-sm text-muted-foreground">{{
            t('pages.email.emailTemplateSelectorModal.noTemplates')
          }}</span>
        </div>

        <div v-else class="grid grid-cols-3 gap-5 pb-8">
          <BaseTemplateCard
            v-for="template in templates"
            :key="template.id"
            icon="IconoirBookmark"
            :title="template.name"
            description=""
            :button-text="t('pages.email.emailTemplateSelectorModal.useTemplate')"
            :processing="creatingTemplateId === template.id"
            :processing-label="t('pages.email.emailTemplateSelectorModal.loading')"
            class="h-full"
            @click="handleSelectTemplate(template.id)"
            @action="handleSelectTemplate(template.id)"
          >
            <template #preview>
              <div class="overflow-hidden rounded-xl border border-border bg-muted mt-2" style="max-height: 190px">
                <EmailTemplatePreview :template-id="template.id" />
              </div>
            </template>
          </BaseTemplateCard>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
